<!--
  HygienecodeJaarschema.vue  –  Bootstrap-Vue 2 edition
  ═══════════════════════════════════════════════════════════════════════════════
  Based on: Hygiënecode Boerderijzuivelbereiding v1.1 (2026)
            Bond van Boerderijzuivelbereiders
  ───────────────────────────────────────────────────────────────────────────────
  DEPENDENCIES
    vue            ^2.6
    bootstrap-vue  ^2.23   (registers all components globally, or import per use)
    bootstrap      ^4.6    (CSS)

  REGISTRATION (main.js)
    import Vue from 'vue'
    import { BootstrapVue } from 'bootstrap-vue'
    import 'bootstrap/dist/css/bootstrap.min.css'
    import 'bootstrap-vue/dist/bootstrap-vue.css'
    Vue.use(BootstrapVue)

  ─── BOOTSTRAP-VUE COMPONENTS USED ────────────────────────────────────────────
    <b-navbar>          Top navigation bar (dark variant)
    <b-navbar-brand>    Logo / title slot inside navbar
    <b-navbar-nav>      Nav item group (action buttons)
    <b-nav-item>        Individual nav action
    <b-button>          Print + CSV export buttons
    <b-tabs>            Schema-type selector (4 tabs)
    <b-tab>             Individual tab panel (one per schema variant)
    <b-alert>           Warning bar (Listeria warning for soft cheese)
    <b-table>           Main compliance schedule table
    <b-badge>           Category pill + frequency badge
    <b-card>            Legend footer card
    <b-card-text>       Text block inside legend card

  ─── MODEL STRUCTURE ───────────────────────────────────────────────────────────
  data()
  ├── activeTabIndex    Number              Index of the active b-tabs tab (0-3)
  ├── months            String[12]          Jan … Dec
  ├── schemaTypes       SchemaType[]        4 variants: 2 cheese types × 2 farm sizes
  │     id, label, sublabel, description, warning, icon, color, pale, light
  ├── sections          Section[]           9 compliance categories
  │   └── rows          Row[]
  │         category    String              Short label (badge)
  │         activity    String              Full task description
  │         note?       String              Optional footnote
  │         freq        { [schemaId]: String }  Frequency per schema
  │         norm        String              Regulatory limit
  │         source      String              Legal reference
  └── legend            LegendItem[]        Footer legend entries

  ─── COMPUTED ──────────────────────────────────────────────────────────────────
  currentSchema         SchemaType          Active schema object (derived from activeTabIndex)
  currentMonth          Number              0-based index of today's month
  tableFields           BvTableField[]      Column definitions fed to <b-table :fields>
  tableItems            BvTableItem[]       Flat row array fed to <b-table :items>
                                            (section header rows + data rows interleaved)

  ─── METHODS ───────────────────────────────────────────────────────────────────
  activeMonths(freq)            → Number[]   1-based active month numbers for a freq string
  isContinuous(freq)            → Boolean    True for daily/weekly/per-production tasks
  isActiveMonth(freq, monthNum) → Boolean    Whether a given month is active
  cellVariant(freq, monthNum)   → String     Bootstrap table cell variant ('success'|'warning'|'')
  monthSymbol(freq, monthNum)   → String     '✓', '●' or ''
  exportToCsv()                 → void       Downloads active schema as UTF-8 CSV
  printSchema()                 → void       window.print()
-->

<template>
    <div class="hc-root" v-if="isLoggedIn">
        <!-- ═══════════════════════════════════════════════════════════
         NAVBAR
    ═══════════════════════════════════════════════════════════ -->
        <b-navbar variant="dark" class="hc-navbar px-3 py-2" sticky>
            <b-navbar-brand class="d-flex align-items-center gap-2 mr-auto">
                <span class="hc-logo-icon mr-2">⚖</span>
                <div>
                    <div class="hc-navbar-title">Hygiënecode Jaarschema</div>
                    <div class="hc-navbar-sub">
                        Boerderijzuivelbereiding v1.1 · 2026
                    </div>
                </div>
            </b-navbar-brand>

            <b-navbar-nav class="ml-auto flex-row">
                <b-button
                    variant="outline-light"
                    size="sm"
                    class="mr-2"
                    @click="printSchema"
                >
                    🖨 Afdrukken
                </b-button>
                <b-button
                    variant="outline-light"
                    size="sm"
                    @click="exportToCsv"
                >
                    ⬇ CSV export
                </b-button>
            </b-navbar-nav>
        </b-navbar>

        <!-- ═══════════════════════════════════════════════════════════
         SCHEMA TABS  (one b-tab per SchemaType)
    ═══════════════════════════════════════════════════════════ -->
        <b-tabs
            v-model="activeTabIndex"
            nav-wrapper-class="hc-tabs-wrapper"
            nav-class="hc-tabs-nav px-3"
            no-fade
        >
            <b-tab
                v-for="schema in schemaTypes"
                :key="schema.id"
                :title-link-class="[
                    'hc-tab-link',
                    activeTabIndex === schemaTypes.indexOf(schema)
                        ? 'hc-tab-link--active'
                        : ''
                ]"
            >
                <!-- Custom tab title slot -->
                <template #title>
                    <div class="hc-tab-title">
                        <span class="hc-tab-icon">{{ schema.icon }}</span>
                        <span class="hc-tab-label">{{ schema.label }}</span>
                        <span class="hc-tab-sub">{{ schema.sublabel }}</span>
                    </div>
                </template>

                <!-- ── INFO ALERT ── -->
                <b-alert
                    show
                    class="hc-infobar mb-0 rounded-0"
                    :style="{
                        borderLeft: '5px solid ' + schema.color,
                        background: schema.pale,
                        borderTop: 'none',
                        borderRight: 'none',
                        borderBottom: '1px solid #dee2e6'
                    }"
                >
                    <div class="d-flex align-items-center flex-wrap gap-2">
                        <strong :style="{ color: schema.color }">{{
                            schema.label
                        }}</strong>
                        <span class="text-muted small ml-2">{{
                            schema.description
                        }}</span>
                        <b-badge
                            v-if="schema.warning"
                            variant="danger"
                            class="ml-2 hc-warning-badge"
                        >
                            ⚠ {{ schema.warning }}
                        </b-badge>
                    </div>
                </b-alert>

                <!-- ── MAIN TABLE ── -->
                <div class="hc-table-wrap px-3 pt-3 pb-2">
                    <b-table
                        :items="tableItems"
                        :fields="tableFields"
                        small
                        bordered
                        hover
                        responsive
                        class="hc-table mb-0"
                        :tbody-tr-class="rowClass"
                    >
                        <!-- Section header rows (item._isSection = true) -->
                        <template #cell(category)="{ item }">
                            <template v-if="item._isSection">
                                <!-- rendered via full-row slot below -->
                            </template>
                            <template v-else>
                                <b-badge
                                    pill
                                    variant="secondary"
                                    class="hc-cat-badge"
                                >
                                    {{ item.category }}
                                </b-badge>
                            </template>
                        </template>

                        <!-- Full-width section header via top-row slot trick: handled in row class + cell override -->
                        <!-- We use a custom row slot to render section rows spanning all columns -->
                        <!-- template #row-details="{ item }">
              <unused – section rows are handled via _isSection + rowClass >
            </template-->

                        <!-- Activity column -->
                        <template #cell(activity)="{ item }">
                            <template v-if="item._isSection">
                                <span class="hc-section-title">
                                    {{ item._sectionIcon }}
                                    {{ item._sectionTitle }}
                                </span>
                            </template>
                            <template v-else>
                                <div>{{ item.activity }}</div>
                                <div v-if="item.note" class="hc-row-note">
                                    {{ item.note }}
                                </div>
                            </template>
                        </template>

                        <!-- Frequency column -->
                        <template #cell(freq)="{ item }">
                            <template v-if="!item._isSection">
                                <b-badge
                                    class="hc-freq-badge"
                                    :style="{
                                        color: currentSchema.color,
                                        borderColor: currentSchema.color,
                                        background: '#fff',
                                        border: '1.5px solid'
                                    }"
                                >
                                    {{ item.freq }}
                                </b-badge>
                            </template>
                        </template>

                        <!-- Norm column -->
                        <template #cell(norm)="{ item }">
                            <template v-if="!item._isSection">
                                <div class="hc-norm">{{ item.norm }}</div>
                                <div class="hc-source">{{ item.source }}</div>
                            </template>
                        </template>

                        <!-- Month columns (Jan … Dec) generated dynamically -->
                        <template
                            v-for="(month, mi) in months"
                            v-slot:[`cell(month_${mi})`]="{ item }"
                        >
                            <template v-if="!item._isSection">
                                <span
                                    :key="month"
                                    class="hc-month-symbol"
                                    :style="
                                        isActiveMonth(item.freq, mi + 1)
                                            ? { color: currentSchema.color }
                                            : {}
                                    "
                                >
                                    {{ monthSymbol(item.freq, mi + 1) }}
                                </span>
                            </template>
                        </template>
                    </b-table>
                </div>
            </b-tab>
        </b-tabs>

        <!-- ═══════════════════════════════════════════════════════════
         LEGEND CARD
    ═══════════════════════════════════════════════════════════ -->
        <div class="px-3 pb-4">
            <b-card
                class="hc-legend-card"
                header-bg-variant="secondary"
                header-text-variant="white"
            >
                <template #header>
                    <strong>Legenda</strong>
                </template>

                <b-card-text>
                    <div class="d-flex flex-wrap" style="gap: 10px 28px">
                        <div
                            v-for="item in legend"
                            :key="item.symbol"
                            class="d-flex align-items-center"
                            style="gap: 8px"
                        >
                            <b-badge
                                variant="light"
                                class="hc-legend-sym border"
                                >{{ item.symbol }}</b-badge
                            >
                            <span class="small text-muted">{{
                                item.desc
                            }}</span>
                        </div>
                    </div>
                </b-card-text>

                <template #footer>
                    <small class="text-muted">
                        Gebaseerd op: Hygiënecode Boerderijzuivelbereiding v1.1
                        (2026), Bond van Boerderijzuivelbereiders. EU 853/2004 ·
                        EU 2073/2005 · Warenwetbesluit Bereiding en behandeling
                        van levensmiddelen. Dit schema is indicatief; raadpleeg
                        altijd de volledige Hygiënecode.
                    </small>
                </template>
            </b-card>
        </div>
    </div>
</template>

<script>
export default {
    name: 'HygienecodeJaarschema',

    // ──────────────────────────────────────────────────────────────────────────
    // DATA
    // ──────────────────────────────────────────────────────────────────────────
    data() {
        return {
            /**
             * Index of the currently active tab.
             * Maps 1:1 to schemaTypes array: 0 = halfhard_klein, 1 = halfhard_midden,
             * 2 = zacht_klein, 3 = zacht_midden.
             * @type {number}
             */
            activeTabIndex: 0,

            /**
             * Month abbreviations. Index 0 = January.
             * @type {string[]}
             */
            months: [
                'Jan',
                'Feb',
                'Mrt',
                'Apr',
                'Mei',
                'Jun',
                'Jul',
                'Aug',
                'Sep',
                'Okt',
                'Nov',
                'Dec'
            ],

            /**
             * The four schema variants (cheese type × farm size).
             * @typedef  {Object}  SchemaType
             * @property {string}  id          Unique key; used as keys in Row.freq
             * @property {string}  label       Full display label
             * @property {string}  sublabel    Short size description for tab
             * @property {string}  description Longer description for info bar
             * @property {string|null} warning Optional warning text (b-badge danger)
             * @property {string}  icon        Emoji for tab title
             * @property {string}  color       Brand hex color
             * @property {string}  pale        Light tint (info bar background)
             * @property {string}  light       Mid tint (active month cell background)
             * @type {SchemaType[]}
             */
            schemaTypes: [
                {
                    id: 'halfhard_klein',
                    label: 'Halfharde/harde kaas – Klein',
                    sublabel: '< 25.000 kg/jaar',
                    description:
                        'Halfharde en harde kaas van rauwe melk. Kleine kaasmakerij (< 25.000 kg product per jaar).',
                    warning: null,
                    icon: '🧀',
                    color: '#1B5E20',
                    pale: '#E8F5E9',
                    light: '#A5D6A7'
                },
                {
                    id: 'halfhard_midden',
                    label: 'Halfharde/harde kaas – Middelgroot',
                    sublabel: '25.000 – 150.000 kg/jaar',
                    description:
                        'Halfharde en harde kaas van rauwe melk. Middelgrote kaasmakerij (25.000–150.000 kg/jaar).',
                    warning: null,
                    icon: '🧀',
                    color: '#E65100',
                    pale: '#FFF3E0',
                    light: '#FFCC80'
                },
                {
                    id: 'zacht_klein',
                    label: 'Zachte kaas – Klein',
                    sublabel: '≤ 25.000 kg/jaar',
                    description:
                        'Zachte kaas met rode korstflora of schimmelculturen (rauwe melk). Kleine kaasmakerij.',
                    warning:
                        'Omgevingsonderzoek Listeria VERPLICHT 4× per jaar. Aanvullend beheersplan Listeria vereist (categorie 1.2).',
                    icon: '🫙',
                    color: '#880E4F',
                    pale: '#FCE4EC',
                    light: '#F48FB1'
                },
                {
                    id: 'zacht_midden',
                    label: 'Zachte kaas – Middelgroot',
                    sublabel: '> 25.000 kg/jaar',
                    description:
                        'Zachte kaas met rode korstflora of schimmelculturen (rauwe melk). Middelgrote kaasmakerij.',
                    warning:
                        'Omgevingsonderzoek Listeria VERPLICHT 4× per jaar. Aanvullend beheersplan Listeria vereist (categorie 1.2).',
                    icon: '🫙',
                    color: '#4A148C',
                    pale: '#EDE7F6',
                    light: '#CE93D8'
                }
            ],

            /**
             * Compliance sections, each containing an array of rows.
             *
             * @typedef  {Object}    Section
             * @property {string}    id      Unique section key
             * @property {string}    title   Display title (without icon)
             * @property {string}    icon    Emoji prefix
             * @property {string}    color   Header background color
             * @property {Row[]}     rows
             *
             * @typedef  {Object}             Row
             * @property {string}             category  Short category label (b-badge pill)
             * @property {string}             activity  Full task description
             * @property {string}             [note]    Optional footnote text
             * @property {Object.<string,string>} freq  Frequency keyed by schemaType.id
             * @property {string}             norm      Regulatory target / limit
             * @property {string}             source    Legal reference string
             *
             * @type {Section[]}
             */
            sections: [
                // ── 1. GRONDSTOF RAUWE MELK – wettelijk ──────────────────────────
                {
                    id: 'grondstof_wettelijk',
                    title: 'Grondstof rauwe melk – Wettelijk verplicht',
                    icon: '🥛',
                    color: '#0D47A1',
                    rows: [
                        {
                            category: 'Rauwe melk',
                            activity:
                                'Celgetal koemelk (voortschrijdend meetkundig gemiddelde)',
                            freq: {
                                halfhard_klein: '1x/maand',
                                halfhard_midden: '1x/maand',
                                zacht_klein: '1x/maand',
                                zacht_midden: '1x/maand'
                            },
                            norm: '≤ 400.000 /ml',
                            source: 'EU 853/2004'
                        },
                        {
                            category: 'Rauwe melk',
                            activity:
                                'Kiemgetal koemelk (voortschrijdend meetkundig gemiddelde)',
                            freq: {
                                halfhard_klein: '2x/maand',
                                halfhard_midden: '2x/maand',
                                zacht_klein: '2x/maand',
                                zacht_midden: '2x/maand'
                            },
                            norm: '≤ 100.000 /ml',
                            source: 'EU 853/2004'
                        },
                        {
                            category: 'Rauwe melk',
                            activity: 'Residuen groeiremmende stoffen',
                            freq: {
                                halfhard_klein: '2x/maand',
                                halfhard_midden: '2x/maand',
                                zacht_klein: '2x/maand',
                                zacht_midden: '2x/maand'
                            },
                            norm: 'Niet aantoonbaar',
                            source: 'EU 853/2004'
                        }
                    ]
                },

                // ── 2. AANVULLEND GRONDSTOFONDERZOEK ─────────────────────────────
                {
                    id: 'grondstof_aanvullend',
                    title: 'Aanvullend grondstofonderzoek (rauwe melk)',
                    icon: '🧫',
                    color: '#1565C0',
                    rows: [
                        {
                            category: 'Grondstof',
                            activity: 'E. coli in rauwe melk',
                            freq: {
                                halfhard_klein: '2x/jaar',
                                halfhard_midden: '4x/jaar',
                                zacht_klein: '2x/jaar',
                                zacht_midden: '4x/jaar'
                            },
                            norm: '≤ 10 kve/ml',
                            source: 'Risico-analyse'
                        },
                        {
                            category: 'Grondstof',
                            activity:
                                'Coagulase-positieve Staphylococcen in rauwe melk',
                            freq: {
                                halfhard_klein: '2x/jaar',
                                halfhard_midden: '4x/jaar',
                                zacht_klein: '2x/jaar',
                                zacht_midden: '4x/jaar'
                            },
                            norm: '≤ 100 kve/ml',
                            source: 'Risico-analyse'
                        },
                        {
                            category: 'Grondstof',
                            activity: 'Antistoffen Salmonella (koemelk)',
                            freq: {
                                halfhard_klein: '3x/jaar',
                                halfhard_midden: '3x/jaar',
                                zacht_klein: '3x/jaar',
                                zacht_midden: '3x/jaar'
                            },
                            norm: 'Onverdacht',
                            source: 'Risico-analyse'
                        }
                    ]
                },

                // ── 3. PRODUCTONDERZOEK HALFHARDE KAAS ───────────────────────────
                {
                    id: 'product_halfhard',
                    title: 'Productonderzoek – Halfharde/harde kaas (rauwe melk) [10–14 dagen na productie]',
                    icon: '🧀',
                    color: '#2E7D32',
                    rows: [
                        {
                            category: 'Kaas product',
                            activity: 'Listeria monocytogenes (n=5)',
                            freq: {
                                halfhard_klein: '2x/jaar',
                                halfhard_midden: '3x/jaar',
                                zacht_klein: '–',
                                zacht_midden: '–'
                            },
                            norm: 'Niet aantoonbaar in 25g',
                            source: 'EU 2073/2005'
                        },
                        {
                            category: 'Kaas product',
                            activity:
                                'Coagulase-positieve Staphylococcen (n=5)',
                            freq: {
                                halfhard_klein: '3x/jaar',
                                halfhard_midden: '6x/jaar',
                                zacht_klein: '–',
                                zacht_midden: '–'
                            },
                            norm: 'm=10.000 / M=100.000 kve/g',
                            source: 'EU 2073/2005'
                        },
                        {
                            category: 'Kaas product',
                            activity: 'Salmonella (n=5)',
                            freq: {
                                halfhard_klein: '2x/jaar',
                                halfhard_midden: '3x/jaar',
                                zacht_klein: '–',
                                zacht_midden: '–'
                            },
                            norm: 'Niet aantoonbaar in 25g',
                            source: 'EU 2073/2005'
                        },
                        {
                            category: 'Kaas product',
                            activity: 'STEC (n=5)',
                            freq: {
                                halfhard_klein: '2x/jaar',
                                halfhard_midden: '3x/jaar',
                                zacht_klein: '–',
                                zacht_midden: '–'
                            },
                            norm: 'Niet aantoonbaar in 25g',
                            source: 'Warenwetbesluit'
                        },
                        {
                            category: 'Kaas product',
                            activity: 'E. coli (n=1)',
                            freq: {
                                halfhard_klein: '3x/jaar',
                                halfhard_midden: '6x/jaar',
                                zacht_klein: '–',
                                zacht_midden: '–'
                            },
                            norm: 'm=100 / M=1.000 kve/g',
                            source: 'Risico-analyse'
                        },
                        {
                            category: 'Kaas product',
                            activity: '(Mesofiele) Lactobacillen (n=1)',
                            freq: {
                                halfhard_klein: '3x/jaar',
                                halfhard_midden: '6x/jaar',
                                zacht_klein: '–',
                                zacht_midden: '–'
                            },
                            norm: '≤ 5 miljoen kve/g',
                            source: 'Risico-analyse'
                        }
                    ]
                },

                // ── 4. PRODUCTONDERZOEK ZACHTE KAAS ──────────────────────────────
                {
                    id: 'product_zacht',
                    title: 'Productonderzoek – Zachte kaas met korstflora/schimmelculturen (rauwe melk)',
                    icon: '🫙',
                    color: '#880E4F',
                    rows: [
                        {
                            category: 'Zachte kaas',
                            activity: 'Listeria monocytogenes (n=5)',
                            freq: {
                                halfhard_klein: '–',
                                halfhard_midden: '–',
                                zacht_klein: '6x/jaar',
                                zacht_midden: '12x/jaar'
                            },
                            norm: 'Niet aantoonbaar in 25g',
                            source: 'EU 2073/2005'
                        },
                        {
                            category: 'Zachte kaas',
                            activity:
                                'Listeria monocytogenes in waswater rode korstflora (n=1) *)',
                            note: '*) Niet nodig als kazen worden besproeid met pekelwaswater.',
                            freq: {
                                halfhard_klein: '–',
                                halfhard_midden: '–',
                                zacht_klein: '3x/jaar',
                                zacht_midden: '6x/jaar'
                            },
                            norm: 'Niet aantoonbaar in 100ml',
                            source: 'Risico-analyse'
                        },
                        {
                            category: 'Zachte kaas',
                            activity:
                                'Coagulase-positieve Staphylococcen (n=5)',
                            freq: {
                                halfhard_klein: '–',
                                halfhard_midden: '–',
                                zacht_klein: '3x/jaar',
                                zacht_midden: '6x/jaar'
                            },
                            norm: 'm=10.000 / M=100.000 kve/g',
                            source: 'EU 2073/2005'
                        },
                        {
                            category: 'Zachte kaas',
                            activity: 'Salmonella (n=5)',
                            freq: {
                                halfhard_klein: '–',
                                halfhard_midden: '–',
                                zacht_klein: '2x/jaar',
                                zacht_midden: '4x/jaar'
                            },
                            norm: 'Niet aantoonbaar in 25g',
                            source: 'EU 2073/2005'
                        },
                        {
                            category: 'Zachte kaas',
                            activity: 'STEC (n=5)',
                            freq: {
                                halfhard_klein: '–',
                                halfhard_midden: '–',
                                zacht_klein: '2x/jaar',
                                zacht_midden: '4x/jaar'
                            },
                            norm: 'Niet aantoonbaar in 25g',
                            source: 'Warenwetbesluit'
                        },
                        {
                            category: 'Zachte kaas',
                            activity: 'E. coli (n=1)',
                            freq: {
                                halfhard_klein: '–',
                                halfhard_midden: '–',
                                zacht_klein: '3x/jaar',
                                zacht_midden: '6x/jaar'
                            },
                            norm: 'm=100 / M=1.000 kve/g',
                            source: 'Risico-analyse'
                        }
                    ]
                },

                // ── 5. ZACHTE ZUURGESTREMDE KAAS ─────────────────────────────────
                {
                    id: 'product_zuurgestremde',
                    title: 'Productonderzoek – Zachte zuurgestremde kaas met korstflora (pH kaas ≤4,4 / korst >4,4)',
                    icon: '🍋',
                    color: '#B71C1C',
                    rows: [
                        {
                            category: 'Zuurgest. kaas',
                            activity: 'Listeria monocytogenes (n=5)',
                            freq: {
                                halfhard_klein: '–',
                                halfhard_midden: '–',
                                zacht_klein: '4x/jaar',
                                zacht_midden: '8x/jaar'
                            },
                            norm: 'Niet aantoonbaar in 25g',
                            source: 'EU 2073/2005'
                        },
                        {
                            category: 'Zuurgest. kaas',
                            activity:
                                'Listeria monocytogenes in waswater (n=1) *)',
                            note: '*) Niet nodig als kazen worden besproeid met pekelwaswater.',
                            freq: {
                                halfhard_klein: '–',
                                halfhard_midden: '–',
                                zacht_klein: '3x/jaar',
                                zacht_midden: '6x/jaar'
                            },
                            norm: 'Niet aantoonbaar in 100ml',
                            source: 'Risico-analyse'
                        },
                        {
                            category: 'Zuurgest. kaas',
                            activity: 'Salmonella (n=5)',
                            freq: {
                                halfhard_klein: '–',
                                halfhard_midden: '–',
                                zacht_klein: '4x/jaar',
                                zacht_midden: '8x/jaar'
                            },
                            norm: 'Niet aantoonbaar in 25g',
                            source: 'EU 2073/2005'
                        },
                        {
                            category: 'Zuurgest. kaas',
                            activity:
                                'Coagulase-positieve Staphylococcen (n=5)',
                            freq: {
                                halfhard_klein: '–',
                                halfhard_midden: '–',
                                zacht_klein: '2x/jaar',
                                zacht_midden: '4x/jaar'
                            },
                            norm: 'm=10.000 / M=100.000 kve/g',
                            source: 'EU 2073/2005'
                        },
                        {
                            category: 'Zuurgest. kaas',
                            activity: 'STEC (n=5)',
                            freq: {
                                halfhard_klein: '–',
                                halfhard_midden: '–',
                                zacht_klein: '2x/jaar',
                                zacht_midden: '4x/jaar'
                            },
                            norm: 'Niet aantoonbaar in 25g',
                            source: 'Warenwetbesluit'
                        },
                        {
                            category: 'Zuurgest. kaas',
                            activity: 'E. coli (n=1)',
                            freq: {
                                halfhard_klein: '–',
                                halfhard_midden: '–',
                                zacht_klein: '4x/jaar',
                                zacht_midden: '8x/jaar'
                            },
                            norm: 'm=100 / M=1.000 kve/g',
                            source: 'Risico-analyse'
                        }
                    ]
                },

                // ── 6. OMGEVINGSONDERZOEK ─────────────────────────────────────────
                {
                    id: 'omgeving',
                    title: 'Omgevingsonderzoek – Listeria monocytogenes',
                    icon: '🌿',
                    color: '#1B5E20',
                    rows: [
                        {
                            category: 'Omgeving',
                            activity:
                                'Listeria omgevingsonderzoek (min. 10 monsters, swabs/veegdoekjes) – vóór reiniging',
                            freq: {
                                halfhard_klein: '1x/jaar',
                                halfhard_midden: '1x/jaar',
                                zacht_klein: '4x/jaar',
                                zacht_midden: '4x/jaar'
                            },
                            norm: 'Niet aantoonbaar',
                            source: 'Risico-analyse §2.10.3.3'
                        },
                        {
                            category: 'Omgeving',
                            activity:
                                'Beheersplan Listeria monocytogenes aanwezig & actueel',
                            freq: {
                                halfhard_klein: '–',
                                halfhard_midden: '–',
                                zacht_klein: '1x/jaar',
                                zacht_midden: '1x/jaar'
                            },
                            norm: 'Categorie 1.2 product',
                            source: 'Hygiënecode §3.2'
                        }
                    ]
                },

                // ── 7. TEMPERATUURBEHEERSING ──────────────────────────────────────
                {
                    id: 'temp',
                    title: 'Temperatuurbeheersing & Procescontrole',
                    icon: '🌡️',
                    color: '#4A148C',
                    rows: [
                        {
                            category: 'Temperatuur',
                            activity:
                                'Koeltemperatuur rauwe melk controleren & registreren',
                            freq: {
                                halfhard_klein: 'Dagelijks',
                                halfhard_midden: 'Dagelijks',
                                zacht_klein: 'Dagelijks',
                                zacht_midden: 'Dagelijks'
                            },
                            norm: '≤ 6 °C',
                            source: 'EU 853/2004'
                        },
                        {
                            category: 'Temperatuur',
                            activity:
                                'Pekel pH en zoutgehalte controleren & registreren',
                            freq: {
                                halfhard_klein: 'Wekelijks',
                                halfhard_midden: 'Wekelijks',
                                zacht_klein: 'Wekelijks',
                                zacht_midden: 'Wekelijks'
                            },
                            norm: 'pH < 4,8 | > 18°Bé',
                            source: 'Risico-analyse'
                        },
                        {
                            category: 'Temperatuur',
                            activity:
                                'Pekeltemperatuur controleren & registreren (witte kaas zonder zuursel)',
                            freq: {
                                halfhard_klein: '–',
                                halfhard_midden: '–',
                                zacht_klein: 'Wekelijks',
                                zacht_midden: 'Wekelijks'
                            },
                            norm: '≤ 7 °C',
                            source: 'Hygiënecode §8.3.7'
                        },
                        {
                            category: 'Temperatuur',
                            activity:
                                'pH meten tijdens verzuring (elke productie)',
                            freq: {
                                halfhard_klein: '–',
                                halfhard_midden: '–',
                                zacht_klein: 'Elke productie',
                                zacht_midden: 'Elke productie'
                            },
                            norm: '< pH 4,4 (kaas) | > 4,4 (korst)',
                            source: 'Hygiënecode §8.2.5'
                        },
                        {
                            category: 'Temperatuur',
                            activity: 'pH-meter kalibreren & registreren',
                            freq: {
                                halfhard_klein: 'Wekelijks',
                                halfhard_midden: 'Wekelijks',
                                zacht_klein: 'Wekelijks',
                                zacht_midden: 'Wekelijks'
                            },
                            norm: 'Conform spec.',
                            source: 'Hygiënecode'
                        },
                        {
                            category: 'Temperatuur',
                            activity:
                                'Schimmeloplossing dagelijks vers aanmaken (witte/blauwe korst)',
                            freq: {
                                halfhard_klein: '–',
                                halfhard_midden: '–',
                                zacht_klein: 'Dagelijks',
                                zacht_midden: 'Dagelijks'
                            },
                            norm: 'Vers bereid met schoon water',
                            source: 'Hygiënecode §8.3.4'
                        },
                        {
                            category: 'Temperatuur',
                            activity:
                                'Thermometers kalibreren (gekalibreerde referentie)',
                            freq: {
                                halfhard_klein: '1x/jaar',
                                halfhard_midden: '1x/jaar',
                                zacht_klein: '1x/jaar',
                                zacht_midden: '1x/jaar'
                            },
                            norm: 'Conform spec.',
                            source: 'Hygiënecode §2.14'
                        },
                        {
                            category: 'Temperatuur',
                            activity: 'Melktap onderhoud door leverancier',
                            freq: {
                                halfhard_klein: '1x/jaar',
                                halfhard_midden: '1x/jaar',
                                zacht_klein: '1x/jaar',
                                zacht_midden: '1x/jaar'
                            },
                            norm: 'Volledig onderhoud',
                            source: 'Hygiënecode §5.1'
                        }
                    ]
                },

                // ── 8. REINIGING & DESINFECTIE ────────────────────────────────────
                {
                    id: 'rd',
                    title: 'Reiniging & Desinfectie',
                    icon: '🧹',
                    color: '#00695C',
                    rows: [
                        {
                            category: 'R&D',
                            activity:
                                'Reiniging & desinfectie bereidingsruimte (conform R&D-plan)',
                            freq: {
                                halfhard_klein: 'Elke productie',
                                halfhard_midden: 'Elke productie',
                                zacht_klein: 'Elke productie',
                                zacht_midden: 'Elke productie'
                            },
                            norm: 'Conform R&D-plan',
                            source: 'Hygiënecode §2.3.2'
                        },
                        {
                            category: 'R&D',
                            activity:
                                'Reiniging kaasplanken / rijpingsruimte controleren',
                            freq: {
                                halfhard_klein: 'Regelmatig',
                                halfhard_midden: 'Regelmatig',
                                zacht_klein: 'Regelmatig',
                                zacht_midden: 'Regelmatig'
                            },
                            norm: 'Droog & schimmelvrij',
                            source: 'Hygiënecode §8.3.1'
                        },
                        {
                            category: 'R&D',
                            activity:
                                'CIP-installatie controle (indien aanwezig)',
                            freq: {
                                halfhard_klein: 'Wekelijks',
                                halfhard_midden: 'Wekelijks',
                                zacht_klein: 'Wekelijks',
                                zacht_midden: 'Wekelijks'
                            },
                            norm: 'Conform spec.',
                            source: 'Hygiënecode §2.3.2.2'
                        },
                        {
                            category: 'R&D',
                            activity:
                                'Naalden prikken blauwe kaas reinigen & desinfecteren (RVS)',
                            freq: {
                                halfhard_klein: '–',
                                halfhard_midden: '–',
                                zacht_klein: 'Elke gebruik',
                                zacht_midden: 'Elke gebruik'
                            },
                            norm: 'RVS kwaliteit',
                            source: 'Hygiënecode §8.3.3'
                        }
                    ]
                },

                // ── 9. ADMINISTRATIE ──────────────────────────────────────────────
                {
                    id: 'admin',
                    title: 'Administratie & Registratie',
                    icon: '📋',
                    color: '#4E342E',
                    rows: [
                        {
                            category: 'Administratie',
                            activity:
                                "Bereidingsschema's bijhouden per kaassoort",
                            freq: {
                                halfhard_klein: 'Per productie',
                                halfhard_midden: 'Per productie',
                                zacht_klein: 'Per productie',
                                zacht_midden: 'Per productie'
                            },
                            norm: 'Ingevuld & bewaard',
                            source: 'Hygiënecode §8.2.2'
                        },
                        {
                            category: 'Administratie',
                            activity: 'Koeltemperatuur registreren',
                            freq: {
                                halfhard_klein: 'Wekelijks',
                                halfhard_midden: 'Wekelijks',
                                zacht_klein: 'Wekelijks',
                                zacht_midden: 'Wekelijks'
                            },
                            norm: 'Schriftelijk/digitaal',
                            source: 'EU 853/2004'
                        },
                        {
                            category: 'Administratie',
                            activity:
                                'Analyseresultaten evalueren & bewaren (min. 2 jaar)',
                            freq: {
                                halfhard_klein: 'Na ontvangst',
                                halfhard_midden: 'Na ontvangst',
                                zacht_klein: 'Na ontvangst',
                                zacht_midden: 'Na ontvangst'
                            },
                            norm: 'Zie beslisboom H.10',
                            source: 'Hygiënecode §2.10.5'
                        },
                        {
                            category: 'Administratie',
                            activity:
                                'Jaarlijkse melding RVO (rauwe melk verkoop)',
                            freq: {
                                halfhard_klein: '1x/jaar',
                                halfhard_midden: '1x/jaar',
                                zacht_klein: '1x/jaar',
                                zacht_midden: '1x/jaar'
                            },
                            norm: 'Via gecombineerde opgave',
                            source: 'Warenwetbesluit §5'
                        },
                        {
                            category: 'Administratie',
                            activity: 'COKZ aanmelding / inspectie gereedmaken',
                            freq: {
                                halfhard_klein: '1x/jaar',
                                halfhard_midden: '1x/jaar',
                                zacht_klein: '1x/jaar',
                                zacht_midden: '1x/jaar'
                            },
                            norm: 'Actuele documentatie',
                            source: 'Hygiënecode §1.5'
                        }
                    ]
                }
            ],

            /**
             * Legend entries shown in the footer card.
             * @type {{ symbol: string, desc: string }[]}
             */
            legend: [
                {
                    symbol: '✓',
                    desc: 'Verplicht laboratoriumonderzoek – monsters inzenden naar ISO 17025 geaccrediteerd lab'
                },
                {
                    symbol: '●',
                    desc: 'Doorlopende monitoring – dagelijks, wekelijks of per productie'
                },
                {
                    symbol: '–',
                    desc: 'Niet van toepassing voor dit kaastype / deze bedrijfsgrootte'
                },
                {
                    symbol: 'n=5',
                    desc: 'Vijf deelmonsters vereist; pathogenen mogen worden gepoold'
                },
                {
                    symbol: '4×/jaar',
                    desc: 'Zachte kaas: omgevingsonderzoek Listeria minimaal 4× per jaar'
                },
                {
                    symbol: 'Beheersplan',
                    desc: 'Aanvullend Listeria beheersplan verplicht voor categorie 1.2-producten (§3.2)'
                }
            ]
        };
    },

    // ──────────────────────────────────────────────────────────────────────────
    // COMPUTED
    // ──────────────────────────────────────────────────────────────────────────
    computed: {
        isLoggedIn() {
            const loggedIn =
                this.$store.getters['account/hasValidatedJWTToken'];
            return loggedIn;
        },
        /**
         * The SchemaType object that corresponds to the currently active tab.
         * @returns {SchemaType}
         */
        currentSchema() {
            return this.schemaTypes[this.activeTabIndex] || this.schemaTypes[0];
        },

        /**
         * The 0-based index of the current calendar month (used to highlight
         * the current month column header in the table).
         * @returns {number}  0 = January … 11 = December
         */
        currentMonth() {
            return new Date().getMonth();
        },

        /**
         * Column definitions for <b-table :fields>.
         * Fixed columns (category, activity, freq, norm) + 12 dynamic month columns.
         *
         * Each month column is keyed as `month_0` … `month_11` so the dynamic
         * v-slot names can be resolved without spaces or special characters.
         *
         * @returns {import('bootstrap-vue').BvTableField[]}
         */
        tableFields() {
            const fixed = [
                {
                    key: 'category',
                    label: 'Categorie',
                    thClass: 'hc-th hc-th--cat',
                    tdClass: 'hc-td hc-td--cat'
                },
                {
                    key: 'activity',
                    label: 'Activiteit / Onderzoek',
                    thClass: 'hc-th hc-th--act',
                    tdClass: 'hc-td hc-td--act'
                },
                {
                    key: 'freq',
                    label: 'Frequentie',
                    thClass: 'hc-th hc-th--freq',
                    tdClass: 'hc-td hc-td--freq'
                },
                {
                    key: 'norm',
                    label: 'Norm',
                    thClass: 'hc-th hc-th--norm',
                    tdClass: 'hc-td hc-td--norm'
                }
            ];
            const monthCols = this.months.map((m, i) => ({
                key: `month_${i}`,
                label: m,
                thClass: [
                    'hc-th',
                    'hc-th--month',
                    i === this.currentMonth ? 'hc-th--month-now' : ''
                ],
                tdClass: 'hc-td hc-td--month'
            }));
            return [...fixed, ...monthCols];
        },

        /**
         * Flat row array for <b-table :items>.
         * Interleaves section-header sentinel rows (marked with _isSection: true)
         * between the data rows of each section. Bootstrap-Vue's b-table renders
         * every item as a <tr>; section rows are visually differentiated via
         * rowClass() + custom cell slots.
         *
         * @returns {Object[]}
         */
        tableItems() {
            const schemaId = this.currentSchema.id;
            const items = [];
            this.sections.forEach((section) => {
                // Section header sentinel
                items.push({
                    _isSection: true,
                    _sectionId: section.id,
                    _sectionTitle: section.title,
                    _sectionIcon: section.icon,
                    _sectionColor: section.color
                });
                // Data rows – pick frequency for the active schema
                section.rows.forEach((row, ri) => {
                    items.push({
                        _isSection: false,
                        _rowIndex: ri,
                        category: row.category,
                        activity: row.activity,
                        note: row.note || null,
                        freq: row.freq[schemaId],
                        norm: row.norm,
                        source: row.source
                    });
                });
            });
            return items;
        }
    },

    // ──────────────────────────────────────────────────────────────────────────
    // METHODS
    // ──────────────────────────────────────────────────────────────────────────
    methods: {
        /**
         * Returns the 1-based month numbers that are active for a given frequency string.
         *
         * Fixed-count frequencies (e.g. "2x/jaar") are spread evenly across the year.
         * Continuous frequencies (daily, weekly, per-production, etc.) are active in
         * all twelve months.
         *
         * @param  {string}   freq  Frequency string from Row.freq
         * @returns {number[]}      1-based active month numbers
         */
        activeMonths(freq) {
            const map = {
                '1x/jaar': [6],
                '2x/jaar': [3, 9],
                '3x/jaar': [1, 5, 9],
                '4x/jaar': [1, 4, 7, 10],
                '6x/jaar': [1, 3, 5, 7, 9, 11],
                '8x/jaar': [1, 2, 4, 5, 7, 8, 10, 11],
                '12x/jaar': [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
            };
            if (map[freq]) return map[freq];
            if (this.isContinuous(freq))
                return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
            return []; // '–' or unknown
        },

        /**
         * Returns true when the frequency represents a task that recurs continuously
         * throughout the year (daily, weekly, per-production, etc.).
         * These tasks are shown with a filled dot (●) rather than a checkmark (✓).
         *
         * @param  {string}  freq
         * @returns {boolean}
         */
        isContinuous(freq) {
            const continuousSet = new Set([
                'Dagelijks',
                'Wekelijks',
                'Regelmatig',
                'Elke productie',
                'Elke gebruik',
                'Per productie',
                'Na ontvangst',
                '1x/maand',
                '2x/maand',
                'Conform R&D-plan',
                'Conform spec.',
                'Ingevuld & bewaard',
                'Schriftelijk/digitaal'
            ]);
            return continuousSet.has(freq);
        },

        /**
         * Returns true if the given 1-based month number is active for a frequency.
         *
         * @param  {string}  freq
         * @param  {number}  monthNum  1-based (1 = January)
         * @returns {boolean}
         */
        isActiveMonth(freq, monthNum) {
            return this.activeMonths(freq).includes(monthNum);
        },

        /**
         * Returns the symbol to render inside a month cell.
         * '●' for continuous tasks, '✓' for discrete lab checks, '' if inactive.
         *
         * @param  {string}  freq
         * @param  {number}  monthNum  1-based
         * @returns {string}
         */
        monthSymbol(freq, monthNum) {
            if (!this.isActiveMonth(freq, monthNum)) return '';
            return this.isContinuous(freq) ? '●' : '✓';
        },

        /**
         * Returns the Bootstrap-Vue table row class for a given item.
         * Used as the :tbody-tr-class binding on <b-table>.
         *
         * Section header rows get a 'hc-section-row' class (+ inline color via style).
         * Data rows alternate between 'hc-row-even' and 'hc-row-odd'.
         *
         * @param  {Object}  item  The b-table row item
         * @param  {string}  type  Row type string from b-table ('row' | 'row-details' | …)
         * @returns {string}
         */
        rowClass(item, type) {
            if (type !== 'row') return '';
            if (item && item._isSection) return 'hc-section-row';
            return item && item._rowIndex % 2 === 0
                ? 'hc-row-even'
                : 'hc-row-odd';
        },

        /**
         * Triggers the browser's native print dialog for the currently visible schema.
         * @returns {void}
         */
        printSchema() {
            window.print();
        },

        /**
         * Serialises the current schema to a UTF-8 CSV file and triggers a download.
         *
         * Columns: Categorie, Activiteit, Frequentie, Norm, Bron, Jan … Dec
         * Active months are marked with ✓ (discrete) or ● (continuous).
         * A section-header row prefixed with '##' is inserted before each group.
         *
         * @returns {void}
         */
        exportToCsv() {
            const schemaId = this.currentSchema.id;
            const headers = [
                'Categorie',
                'Activiteit',
                'Frequentie',
                'Norm',
                'Bron',
                ...this.months
            ];
            const csvRows = [];

            this.sections.forEach((section) => {
                csvRows.push([
                    `## ${section.icon} ${section.title}`,
                    ...Array(headers.length - 1).fill('')
                ]);
                section.rows.forEach((row) => {
                    const freq = row.freq[schemaId];
                    const monthCells = this.months.map((_, i) =>
                        this.monthSymbol(freq, i + 1)
                    );
                    csvRows.push([
                        row.category,
                        row.activity,
                        freq,
                        row.norm,
                        row.source,
                        ...monthCells
                    ]);
                });
            });

            const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
            const csvContent = [headers, ...csvRows]
                .map((r) => r.map(esc).join(','))
                .join('\n');
            const blob = new Blob(['\uFEFF' + csvContent], {
                type: 'text/csv;charset=utf-8;'
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Jaarschema_${schemaId}_${new Date().getFullYear()}.csv`;
            link.click();
            URL.revokeObjectURL(url);
        }
    }
};
</script>

<style scoped>
/* ── Root ──────────────────────────────────────────────────────────────── */
.hc-root {
    font-size: 13px;
    background: #f4f6f9;
    min-height: 100vh;
}

/* ── Navbar ────────────────────────────────────────────────────────────── */
.hc-navbar {
    background: #111827 !important;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
}

.hc-navbar-title {
    font-size: 17px;
    font-weight: 700;
    color: #f9fafb;
    letter-spacing: -0.2px;
}

.hc-navbar-sub {
    font-size: 11px;
    color: #9ca3af;
    letter-spacing: 0.3px;
}

.hc-logo-icon {
    font-size: 26px;
    line-height: 1;
}

/* ── Tabs wrapper ──────────────────────────────────────────────────────── */
.hc-tabs-wrapper {
    background: #1f2937;
    border-bottom: 1px solid #374151;
}

.hc-tabs-nav {
    flex-wrap: nowrap;
    overflow-x: auto;
}

/* Override Bootstrap nav-tabs styles for dark background */
.hc-tabs-nav .nav-link {
    color: #9ca3af !important;
    border: none !important;
    border-bottom: 3px solid transparent !important;
    border-radius: 0 !important;
    padding: 10px 18px 8px !important;
    background: transparent !important;
    transition: color 0.15s, border-color 0.15s !important;
}

.hc-tabs-nav .nav-link:hover {
    color: #e5e7eb !important;
    background: rgba(255, 255, 255, 0.07) !important;
}

.hc-tabs-nav .nav-link.active {
    color: #fff !important;
    background: rgba(255, 255, 255, 0.06) !important;
    border-bottom-color: #6ee7b7 !important; /* overridden per-tab via activeTabIndex */
}

/* Tab title inner layout */
.hc-tab-title {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 160px;
}

.hc-tab-icon {
    font-size: 18px;
    margin-bottom: 2px;
}
.hc-tab-label {
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
}
.hc-tab-sub {
    font-size: 10px;
    opacity: 0.7;
    margin-top: 1px;
    white-space: nowrap;
}

/* ── Info alert ────────────────────────────────────────────────────────── */
.hc-infobar {
    font-size: 12px;
}

.hc-warning-badge {
    font-size: 11px;
    font-weight: 600;
    white-space: normal;
    text-align: left;
    max-width: 480px;
}

/* ── Table wrapper ─────────────────────────────────────────────────────── */
.hc-table-wrap {
    overflow-x: auto;
}

/* ── b-table overrides ─────────────────────────────────────────────────── */
.hc-table {
    font-size: 12px;
    min-width: 1100px;
}

/* Column widths */
.hc-th--cat {
    width: 110px;
    min-width: 90px;
    white-space: nowrap;
}
.hc-th--act {
    width: 300px;
    min-width: 200px;
    text-align: left !important;
}
.hc-th--freq {
    width: 95px;
    min-width: 80px;
    text-align: center !important;
}
.hc-th--norm {
    width: 175px;
    min-width: 140px;
    text-align: left !important;
}
.hc-th--month {
    width: 44px;
    min-width: 36px;
    text-align: center !important;
    white-space: nowrap;
}

/* Current month highlight */
.hc-th--month-now {
    background: #1f2937 !important;
    color: #fcd34d !important;
}

/* Table header row */
.hc-table >>> thead tr th {
    background: #374151;
    color: #f9fafb;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.4px;
    vertical-align: middle;
    border-color: #4b5563;
}

/* Section header rows */
.hc-section-row td {
    background: #374151; /* overridden via _sectionColor in slot */
    color: #fff !important;
    font-weight: 700;
    font-size: 12px;
    padding: 7px 12px !important;
    border: none !important;
}

.hc-section-title {
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.1px;
}

/* Data rows */
.hc-row-even {
    background: #f9fafb !important;
}
.hc-row-odd {
    background: #ffffff !important;
}

.hc-table >>> tbody tr.hc-row-even:hover,
.hc-table >>> tbody tr.hc-row-odd:hover {
    background: #eff6ff !important;
}

/* TD base */
.hc-td {
    vertical-align: middle !important;
    padding: 6px 8px !important;
    border-color: #e5e7eb !important;
}

.hc-td--cat {
    padding-left: 10px !important;
}
.hc-td--act {
}
.hc-td--freq {
    text-align: center !important;
}
.hc-td--norm {
    font-size: 11px !important;
}
.hc-td--month {
    text-align: center !important;
    font-size: 13px !important;
    font-weight: 700 !important;
    padding: 4px 2px !important;
}

/* ── Category badge ────────────────────────────────────────────────────── */
.hc-cat-badge {
    font-size: 10px !important;
    font-weight: 600 !important;
    background: #e5e7eb !important;
    color: #374151 !important;
    white-space: nowrap;
}

/* ── Freq badge ────────────────────────────────────────────────────────── */
.hc-freq-badge {
    font-size: 11px !important;
    font-weight: 700 !important;
    padding: 3px 8px !important;
    white-space: nowrap;
}

/* ── Norm / source ─────────────────────────────────────────────────────── */
.hc-norm {
    display: block;
    font-size: 11px;
    color: #111827;
}
.hc-source {
    display: block;
    font-size: 10px;
    color: #6b7280;
    margin-top: 1px;
}

/* ── Row note ──────────────────────────────────────────────────────────── */
.hc-row-note {
    display: block;
    font-size: 10px;
    color: #9ca3af;
    font-style: italic;
    margin-top: 2px;
}

/* ── Month symbol ──────────────────────────────────────────────────────── */
.hc-month-symbol {
    font-size: 13px;
    font-weight: 900;
}

/* ── Legend card ───────────────────────────────────────────────────────── */
.hc-legend-card {
    font-size: 12px;
}

.hc-legend-sym {
    min-width: 60px;
    text-align: center;
    font-weight: 700 !important;
    font-size: 11px !important;
    color: #1f2937 !important;
    background: #f3f4f6 !important;
}

/* ── Print ─────────────────────────────────────────────────────────────── */
@media print {
    .hc-navbar,
    .hc-tabs-wrapper {
        display: none !important;
    }
    .hc-table-wrap {
        padding: 0 !important;
    }
    .hc-root {
        background: #fff !important;
    }
}
</style>
